import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  FiFileText,
  FiPlus,
  FiRotateCcw,
  FiShoppingCart,
  FiClipboard,
  FiUploadCloud,
} from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn, TabFade } from '../components/motion';
import MoreMenu from '../components/MoreMenu';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  StatusBadge,
  formatCents as money,
} from '../components/shared/Ui';
import '../components/erp/Erp.css';
import { appAlert, appConfirm } from '../utils/appDialog';
import ServicesSalesPanel from '../components/services/ServicesSalesPanel';
import { GlPostingStatus, MoneyText } from '../components/commercial/CommercialUi';
import {
  invalidateCreditNotes,
  queryKeys,
  staleTimes,
} from '../config/queryKeys';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const TABS = [
  { id: 'documents', label: 'Quotes & invoices', icon: FiFileText },
  { id: 'client-pos', label: 'Client POs', icon: FiClipboard },
  { id: 'credits', label: 'Credit notes', icon: FiRotateCcw },
  { id: 'orders', label: 'Sales order history', icon: FiShoppingCart },
];

const SellingErp = () => {
  const { hasPermission } = usePermissions();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(
    TABS.some((item) => item.id === requestedTab) ? requestedTab : 'documents'
  );

  useEffect(() => {
    if (TABS.some((item) => item.id === requestedTab)) setTab(requestedTab);
  }, [requestedTab]);

  const orders = useQuery(
    queryKeys.sellingOrders({ division: 'professional' }),
    async () =>
      (await api.get('/selling/orders', { params: { division: 'professional' } })).data,
    { enabled: tab === 'orders' }
  );
  const notes = useQuery(
    queryKeys.creditNotes(),
    async () => (await api.get('/selling/credit-notes')).data,
    { enabled: tab === 'credits' }
  );
  const clientPos = useQuery(
    queryKeys.clientOrders(),
    async () => (await api.get('/purchase-orders')).data,
    { enabled: tab === 'client-pos', staleTime: staleTimes.lists }
  );

  const postCredit = useMutation(
    async (id) => (await api.post(`/selling/credit-notes/${id}/post`)).data,
    {
      onSuccess: () => {
        invalidateCreditNotes(qc);
        qc.invalidateQueries(['erp-report']);
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const postOrderGl = useMutation(
    async (salesDocumentId) =>
      (await api.post(`/accounting/sales-documents/${salesDocumentId}/post-to-gl`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.sellingOrders({ division: 'professional' }));
        qc.invalidateQueries(queryKeys.salesDocuments());
        qc.invalidateQueries(['erp-report']);
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const unpostOrderGl = useMutation(
    async (salesDocumentId) =>
      (await api.post(`/accounting/sales-documents/${salesDocumentId}/unpost-from-gl`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.sellingOrders({ division: 'professional' }));
        qc.invalidateQueries(queryKeys.salesDocuments());
        qc.invalidateQueries(['erp-report']);
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const unpostCredit = useMutation(
    async (id) => (await api.post(`/selling/credit-notes/${id}/unpost`)).data,
    {
      onSuccess: () => {
        invalidateCreditNotes(qc);
        qc.invalidateQueries(['erp-report']);
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const createOrderInvoice = useMutation(
    async (orderId) => (await api.post(`/selling/orders/${orderId}/create-invoice`)).data,
    {
      onSuccess: (data) => {
        qc.invalidateQueries(queryKeys.sellingOrders({ division: 'professional' }));
        qc.invalidateQueries(queryKeys.salesDocuments());
        void appAlert(
          data.already_existed
            ? `Invoice ${data.document?.number || ''} already linked to this order.`
            : `Invoice ${data.document?.number || ''} created. You can Post to GL when ready.`
        );
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const orderRows = orders.data || [];
  const noteRows = notes.data || [];
  const clientPoRows = clientPos.data || [];
  const {
    pageItems: pagedClientPoRows,
    pagination: clientPoPagination,
    setPage: setClientPoPage,
  } = useClientPager(clientPoRows);
  const {
    pageItems: pagedOrderRows,
    pagination: orderPagination,
    setPage: setOrderPage,
  } = useClientPager(orderRows);
  const {
    pageItems: pagedNoteRows,
    pagination: notePagination,
    setPage: setNotePage,
  } = useClientPager(noteRows);
  const canPostGl = hasPermission('erp_journals_post');
  const canCreateInvoice =
    hasPermission('erp_selling_manage') || hasPermission('service_sales_manage');

  const changeTab = (nextTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    if (nextTab !== 'documents') next.delete('document');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1>
            <FiFileText aria-hidden /> Sales
          </h1>
          <p className="page-subtitle">
            Quotes, invoices, Client POs, credit notes, and package sales.
          </p>
        </div>
      </FadeIn>

      <div className="erp-tabs" role="tablist" aria-label="Sales sections">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`erp-tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => changeTab(id)}
          >
            <Icon aria-hidden />
            {label}
          </button>
        ))}
      </div>

      <TabFade tabKey={tab}>
        {tab === 'documents' && (
          <ServicesSalesPanel
            canManage={hasPermission('service_sales_manage')}
            canPostGl={canPostGl}
            focusDocumentId={searchParams.get('document')}
          />
        )}

        {tab === 'client-pos' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Client POs</h2>
                <p className="erp-panel-meta">
                  {clientPoRows.length}{' '}
                  {clientPoRows.length === 1 ? 'Client PO' : 'Client POs'}
                </p>
              </div>
              <Link className="btn btn-secondary btn-sm" to="/orders">
                Open Client POs
              </Link>
            </div>
            <ErpListState
              query={clientPos}
              isEmpty={clientPoRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiClipboard}
                  title="No Client POs"
                  hint="Approve a requisition to draft a Client PO, then manage it under Client POs."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Status</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedClientPoRows.map((po) => (
                      <tr key={po.id}>
                        <td className="erp-cell-primary">
                          <Link to={`/orders/${po.id}`}>
                            <span className="erp-code">{po.number}</span>
                          </Link>
                        </td>
                        <td>{po.customer_billing_name || '—'}</td>
                        <td>
                          <StatusBadge status={po.status} />
                        </td>
                        <td>
                          <span className="erp-code">{po.currency || 'USD'}</span>
                        </td>
                        <td className="erp-num">
                          {po.estimated_value == null ? (
                            '—'
                          ) : (
                            <MoneyText
                              amount={po.estimated_value}
                              currency={po.currency}
                              unit="dollars"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={clientPoPagination.page}
                pageCount={clientPoPagination.pageCount}
                total={clientPoPagination.total}
                limit={clientPoPagination.limit}
                onPageChange={setClientPoPage}
              />
            </ErpListState>
          </section>
        )}

        {tab === 'orders' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Sales order history</h2>
                <p className="erp-panel-meta">
                  {orderRows.length} {orderRows.length === 1 ? 'order' : 'orders'} · package
                  sales
                </p>
              </div>
            </div>
            <ErpListState
              query={orders}
              isEmpty={orderRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiShoppingCart}
                  title="No sales orders yet"
                  hint="Record a package payment on a candidate enrollment to create a sales order, then create an invoice and Post to GL."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Package</th>
                      <th scope="col">Sold to</th>
                      <th scope="col">Date</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">
                        Total
                      </th>
                      <th scope="col">Invoice</th>
                      <th scope="col">GL / cash</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="erp-cell-actions">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOrderRows.map((o) => {
                      const canCreate =
                        canCreateInvoice &&
                        !o.sales_document_id &&
                        !['cancelled', 'void', 'canceled'].includes(
                          String(o.status || '').toLowerCase()
                        );
                      const canPost =
                        canPostGl &&
                        o.sales_document_id &&
                        o.gl_status !== 'posted' &&
                        o.invoice_status &&
                        !['draft', 'void', 'rejected'].includes(o.invoice_status);
                      const canUnpost =
                        canPostGl &&
                        o.sales_document_id &&
                        o.gl_status === 'posted' &&
                        o.cash_status !== 'paid' &&
                        o.cash_status !== 'partial';
                      return (
                        <tr key={o.id}>
                          <td className="erp-cell-primary">
                            <span className="erp-code">{o.order_number}</span>
                          </td>
                          <td>{o.package_name || '—'}</td>
                          <td>
                            {o.candidate_id ? (
                              <Link to={`/candidates/${o.candidate_id}`}>
                                {o.party_name || `Candidate #${o.candidate_id}`}
                              </Link>
                            ) : (
                              o.party_name || '—'
                            )}
                          </td>
                          <td>{o.order_date}</td>
                          <td>
                            <span className="erp-code">{o.currency || 'USD'}</span>
                          </td>
                          <td className="erp-num">{money(o.total_cents, o.currency)}</td>
                          <td>
                            {o.sales_document_id && o.invoice_number ? (
                              <Link to={`/sales/documents/${o.sales_document_id}`}>
                                <span className="erp-code">{o.invoice_number}</span>
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <GlPostingStatus
                              status={o.gl_status || 'unposted'}
                              journalEntryNumber={o.journal_entry_number}
                            />
                          </td>
                          <td>
                            <StatusBadge status={o.status} />
                          </td>
                          <td className="erp-cell-actions">
                            <MoreMenu
                              label={`Actions for ${o.order_number}`}
                              size="sm"
                              items={[
                                {
                                  id: 'create-invoice',
                                  label:
                                    createOrderInvoice.isLoading &&
                                    createOrderInvoice.variables === o.id
                                      ? 'Creating…'
                                      : 'Create invoice',
                                  icon: FiFileText,
                                  hidden: !canCreate,
                                  disabled:
                                    createOrderInvoice.isLoading &&
                                    createOrderInvoice.variables === o.id,
                                  onSelect: () => createOrderInvoice.mutate(o.id),
                                },
                                {
                                  id: 'post-gl',
                                  label:
                                    postOrderGl.isLoading &&
                                    postOrderGl.variables === o.sales_document_id
                                      ? 'Posting…'
                                      : 'Post to GL',
                                  icon: FiUploadCloud,
                                  hidden: !canPost,
                                  disabled:
                                    postOrderGl.isLoading &&
                                    postOrderGl.variables === o.sales_document_id,
                                  onSelect: () => postOrderGl.mutate(o.sales_document_id),
                                },
                                {
                                  id: 'unpost-gl',
                                  label:
                                    unpostOrderGl.isLoading &&
                                    unpostOrderGl.variables === o.sales_document_id
                                      ? 'Reverting…'
                                      : 'Revert to draft',
                                  icon: FiRotateCcw,
                                  hidden: !canUnpost,
                                  disabled:
                                    unpostOrderGl.isLoading &&
                                    unpostOrderGl.variables === o.sales_document_id,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      'Revert this invoice from the GL to unposted? You can correct it and post again.'
                                    );
                                    if (ok) unpostOrderGl.mutate(o.sales_document_id);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={orderPagination.page}
                pageCount={orderPagination.pageCount}
                total={orderPagination.total}
                limit={orderPagination.limit}
                onPageChange={setOrderPage}
              />
            </ErpListState>
          </section>
        )}

        {tab === 'credits' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Credit notes</h2>
                <p className="erp-panel-meta">
                  {noteRows.length} {noteRows.length === 1 ? 'credit note' : 'credit notes'}
                </p>
              </div>
              {hasPermission('erp_selling_manage') && (
                <Link className="btn btn-primary btn-sm" to="/sales/credit-notes/new">
                  <FiPlus aria-hidden /> New credit note
                </Link>
              )}
            </div>
            <ErpListState
              query={notes}
              isEmpty={noteRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiRotateCcw}
                  title="No credit notes yet"
                  hint="Issue a credit note to reverse or reduce a posted invoice."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Date</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">
                        Amount
                      </th>
                      <th scope="col">Status</th>
                      <th scope="col">GL / cash</th>
                      <th scope="col" className="erp-cell-actions">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedNoteRows.map((n) => {
                      const canPost =
                        canPostGl && n.status !== 'posted';
                      return (
                        <tr key={n.id}>
                          <td className="erp-cell-primary">
                            <Link to={`/sales/credit-notes/${n.id}`}>
                              <span className="erp-code">{n.credit_number}</span>
                            </Link>
                          </td>
                          <td>
                            {n.customer_billing_name ||
                              (n.customer_id ? (
                                <span className="erp-muted">#{n.customer_id}</span>
                              ) : (
                                '—'
                              ))}
                          </td>
                          <td>{n.credit_date}</td>
                          <td>
                            <span className="erp-code">{n.currency || 'USD'}</span>
                          </td>
                          <td className="erp-num">{money(n.amount_cents, n.currency)}</td>
                          <td>
                            <StatusBadge status={n.status} />
                          </td>
                          <td>
                            <GlPostingStatus
                              status={n.status === 'posted' ? 'posted' : 'unposted'}
                              journalEntryNumber={n.journal_entry_number || n.entry_number}
                            />
                          </td>
                          <td className="erp-cell-actions">
                            <MoreMenu
                              label={`Actions for ${n.credit_number}`}
                              size="sm"
                              items={[
                                {
                                  id: 'post-gl',
                                  label:
                                    postCredit.isLoading && postCredit.variables === n.id
                                      ? 'Posting…'
                                      : 'Post to GL',
                                  icon: FiUploadCloud,
                                  hidden: !canPost,
                                  disabled:
                                    postCredit.isLoading && postCredit.variables === n.id,
                                  onSelect: () => postCredit.mutate(n.id),
                                },
                                {
                                  id: 'unpost-gl',
                                  label:
                                    unpostCredit.isLoading && unpostCredit.variables === n.id
                                      ? 'Reverting…'
                                      : 'Revert to draft',
                                  icon: FiRotateCcw,
                                  hidden: !canPostGl || n.status !== 'posted',
                                  disabled:
                                    unpostCredit.isLoading && unpostCredit.variables === n.id,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      `Revert ${n.credit_number} to draft? Its GL entry will leave the ledger so you can correct it and post again.`
                                    );
                                    if (ok) unpostCredit.mutate(n.id);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={notePagination.page}
                pageCount={notePagination.pageCount}
                total={notePagination.total}
                limit={notePagination.limit}
                onPageChange={setNotePage}
              />
            </ErpListState>
          </section>
        )}
      </TabFade>
    </div>
  );
};

export default SellingErp;
